'use client';

import { useEffect } from 'react';
import { Box, Typography } from '@/primitives';
import useAnimalDisplayType from '@/hooks/studbook/animal/useAnimalDisplayType';
import useSearchStore from '@/studbook/store/useSearchStore';
import Image from 'next/image';
import SearchBreadcrumbs from '@/components/publicSearch/searchPage/SearchBreadcrumbs';
import SearchTabs from '@/components/publicSearch/searchPage/SearchTabs';
import SearchHeader from '@/components/publicSearch/searchPage/SearchHeader';
import { TSearchType } from '@/studbook/store/types';
import useCurrentOrganisation from '@/hooks/studbook/useCurrentOrganisation';
import SearchResult from '@/components/publicSearch/searchPage/SearchResult';
import useSearch from '@/hooks/studbook/useSearch';
import useGridStore from '@/studbook/store/useGridStore';
import { NAV_HEIGHT } from '@/configs/layout';
import { slate } from '@/lib/colours';
import useAnimalFilterStore from '@/studbook/store/useAnimalFilterStore';
import useMemberFilterStore from '@/studbook/store/useMemberFilterStore';

interface SearchPageProps {
  params: {
    orgName: string;
  };
}

export default function SearchPage({ params }: SearchPageProps) {
  const { isMobile, resultDisplay, setResultDisplay } = useAnimalDisplayType();
  const { openResult, searchType, setBasicSearch, setSearchType } =
    useSearchStore();
  const currentOrg = useCurrentOrganisation();
  const {
    paginationModel,
    setManualColumnView,
    setPaginationModel,
    setGridSortModel,
  } = useGridStore();
  const { resetColumnOrder } = useSearch(currentOrg.orgId, paginationModel);
  const { clearFilter: clearAnimalFilters } = useAnimalFilterStore();
  const { clearFilter: clearMemberFilters } = useMemberFilterStore();

  const tabs = [
    { label: 'Animal Enquiry', value: 'animal' },
    { label: 'Member Enquiry', value: 'member' },
    // { label: 'EBV Enquiry', value: 'ebv' },
    // { label: 'Sale Catalogue', value: 'sale' },
  ];

  // clear filters when switching between tabs
  const handleTabChange = (value: string) => {
    clearAnimalFilters();
    clearMemberFilters();
    setBasicSearch('');
    setGridSortModel(
      // TODO: change for i6
      // @ts-ignore: entity detail name across all orgs and envs for pre i6
      value === 'animal' ? [{ field: '10006', sort: 'asc' }] : [],
    );
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setSearchType(value as TSearchType);
  };

  const handleResetColumnDisplay = () => {
    resetColumnOrder && resetColumnOrder();
    setManualColumnView(false);
  };

  useEffect(() => {
    if (!currentOrg.description) return;
    document.title = `${currentOrg.description} - BreedLOGIC Database Search`;
  }, [currentOrg.description]);

  return (
    <Box
      sx={{
        height: `calc(100vh - ${isMobile ? 280 : 250}px - ${NAV_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: slate[50],
      }}
    >
      <SearchBreadcrumbs orgName={params.orgName} />
      <SearchTabs
        tabs={tabs}
        activeTab={searchType}
        onTabChange={handleTabChange}
      />
      <SearchHeader
        isMobile={isMobile}
        resultDisplay={resultDisplay}
        setResultDisplay={setResultDisplay}
        resetColumnDisplay={handleResetColumnDisplay}
      />

      {openResult ? (
        <SearchResult />
      ) : (
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <Image
            src={'/empty-inbox.svg'}
            alt="Empty Box"
            width={70}
            height={100}
          />
          <Typography
            color="text.secondary"
            textAlign="center"
            variant="labellarge"
            paddingBlock={2}
          >
            To get started, enter {searchType} name or identifier into quick
            search bar, or click basic or advanced search.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
